import api from '../api';
import WebAuthnProvider from './WebAuthnProvider';
import WindowsHelloProvider from './WindowsHelloProvider';
import ExternalScannerAdapter from './ExternalScannerAdapter';

/**
 * Enterprise LIMS Biometric & Passkey Authentication Service.
 * Manages provider discovery, registration ceremonies, login verification,
 * and credential management adhering strictly to W3C WebAuthn Level 2/3.
 */
class BiometricAuthService {
  constructor() {
    this.providers = [
      new WindowsHelloProvider(),
      new WebAuthnProvider(),
      new ExternalScannerAdapter(),
    ];
  }

  /**
   * Resolves the primary available authenticator on the current device.
   */
  async getAvailableProvider() {
    for (const provider of this.providers) {
      if (await provider.isAvailable()) {
        return provider;
      }
    }
    // Default to base WebAuthnProvider
    return this.providers[1];
  }

  /**
   * Checks whether the current device supports secure passkey / biometric login.
   */
  async checkBiometricAvailability() {
    try {
      const provider = await this.getAvailableProvider();
      const isAvailable = await provider.isAvailable();
      const deviceInfo = await provider.getDeviceInfo();
      return {
        isAvailable,
        ...deviceInfo,
      };
    } catch {
      return {
        isAvailable: false,
        providerName: 'Unavailable',
        deviceType: 'None',
        supportsBiometrics: false,
      };
    }
  }

  /**
   * Generates options, prompts authenticator ceremony, and submits assertion to server.
   */
  async loginWithPasskey({ username = '', centreId = null, roleId = null } = {}) {
    const provider = await this.getAvailableProvider();
    if (!(await provider.isAvailable())) {
      throw new Error('Secure biometric / passkey login is not supported on this browser or device.');
    }

    // 1. Request authentication options & challenge from server
    const optionsPayload = {};
    if (username && username.trim()) {
      optionsPayload.username = username.trim();
    }
    if (centreId) {
      optionsPayload.centreId = parseInt(centreId, 10);
    }
    if (roleId) {
      optionsPayload.roleId = parseInt(roleId, 10);
    }

    const optionsRes = await api.post('/auth/passkey/login-options', optionsPayload);
    if (!optionsRes.data?.success || !optionsRes.data?.data) {
      throw new Error(optionsRes.data?.message || 'Failed to initiate secure passkey ceremony.');
    }

    const { challengeId, options } = optionsRes.data.data;

    // 2. Perform authenticator ceremony (Fingerprint / Windows Hello / Security Key)
    const assertionResponse = await provider.authenticate(options);

    // 3. Submit cryptographic assertion to server for verification and token generation
    const verifyPayload = {
      challengeId,
      assertionResponseJson: JSON.stringify(assertionResponse),
      centreId: centreId ? parseInt(centreId, 10) : null,
      roleId: roleId ? parseInt(roleId, 10) : null,
    };

    const verifyRes = await api.post('/auth/passkey/login-verify', verifyPayload);
    if (!verifyRes.data?.success || !verifyRes.data?.data) {
      throw new Error(verifyRes.data?.message || 'Biometric authentication verification failed.');
    }

    return verifyRes.data.data;
  }

  /**
   * Registers a new passkey credential for an employee.
   */
  async registerPasskey(employeeId, deviceFriendlyName = '') {
    const provider = await this.getAvailableProvider();
    if (!(await provider.isAvailable())) {
      throw new Error('Passkey / WebAuthn is not supported in this browser or device.');
    }

    // 1. Request registration options & challenge from server
    const optionsRes = await api.post('/auth/passkey/register-options', {
      employeeId: parseInt(employeeId, 10),
      deviceName: deviceFriendlyName,
    });

    if (!optionsRes.data?.success || !optionsRes.data?.data) {
      throw new Error(optionsRes.data?.message || 'Failed to obtain registration challenge from server.');
    }

    const { challengeId, options } = optionsRes.data.data;

    // 2. Perform authenticator enrollment ceremony
    const attestationResponse = await provider.register(options);

    // 3. Submit attestation response to server for cryptographic validation and credential storage
    const verifyRes = await api.post('/auth/passkey/register-verify', {
      challengeId,
      attestationResponseJson: JSON.stringify(attestationResponse),
      deviceFriendlyName: deviceFriendlyName || 'Windows Hello / Passkey',
    });

    if (!verifyRes.data?.success) {
      throw new Error(verifyRes.data?.message || 'Failed to verify and save passkey credential.');
    }

    return verifyRes.data;
  }

  /**
   * Retrieves list of registered credentials for an employee.
   */
  async getEmployeeCredentials(employeeId) {
    const res = await api.get(`/auth/passkey/credentials/${employeeId}`);
    return res.data?.data || [];
  }

  /**
   * Revokes an existing passkey credential.
   */
  async revokeCredential(credentialId, employeeId = null) {
    const res = await api.post('/auth/passkey/revoke', {
      credentialId,
      employeeId: employeeId ? parseInt(employeeId, 10) : null,
    });
    return res.data;
  }
}

export const biometricAuthService = new BiometricAuthService();
export default biometricAuthService;
